import { Head, Link } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
  status: number
}

interface Assignment {
  id: number
  entry_type: "equipment" | "gps"
  equipment_name: string | null
  equipment_type: string | null
  assigned_to: string | null
  assigned_at: string | null
  assigned_at_raw: string | null
  returned_at: string | null
  start_odometer: number | null
  end_odometer: number | null
  odometer_usage: number | null
  requires_odometer: boolean
  billable_amount: number | null
  notes: string | null
  install_vehicle: string | null
  install_vehicle_location: string | null
  install_gps_location: string | null
  remove_vehicle_location: string | null
  removed_by: string | null
  currently_assigned: boolean
  requires_location: boolean
}

interface Summary {
  total: number
  currently_assigned: number
  returned: number
}

interface Props {
  case_record: CaseRecord
  assignments: Assignment[]
  summary: Summary
  can_create: boolean
  can_edit: boolean
}

function OdometerDisplay({ assignment }: { assignment: Assignment }) {
  const { start_odometer, end_odometer, odometer_usage, requires_odometer } =
    assignment
  if (!requires_odometer && start_odometer == null && end_odometer == null) {
    return null
  }
  const startStr =
    start_odometer != null && start_odometer > 0
      ? start_odometer.toLocaleString("en-US", { maximumFractionDigits: 0 })
      : "?"
  const endStr =
    end_odometer != null && end_odometer > 0
      ? end_odometer.toLocaleString("en-US", { maximumFractionDigits: 0 })
      : "?"
  const usageStr =
    odometer_usage != null
      ? ` (${odometer_usage.toLocaleString("en-US", { maximumFractionDigits: 0 })})`
      : ""
  return (
    <div>
      <strong>Odo/Hours:</strong> {startStr}-{endStr}
      {usageStr}
    </div>
  )
}

function AssignmentCard({
  assignment,
  caseId,
  canEdit,
}: {
  assignment: Assignment
  caseId: number
  canEdit: boolean
}) {
  return (
    <li className="border-hpi-light-gray border-b py-3 last:border-b-0">
      <div className="flex items-start justify-between">
        <div>
          <span className="font-semibold">{assignment.equipment_name}</span>{" "}
          <span className="text-hpi-dark-gray">
            Assigned to {assignment.assigned_to ?? "—"} on{" "}
            {assignment.assigned_at ?? "—"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canEdit &&
            assignment.entry_type === "equipment" &&
            assignment.currently_assigned && (
              <Link
                href={`/cases/${caseId}/equipment_assignments/${assignment.id}/edit`}
                className="text-hpi-active-blue hover:text-hpi-blue text-xs font-medium"
              >
                Return
              </Link>
            )}
          {canEdit &&
            assignment.entry_type === "equipment" &&
            !assignment.currently_assigned && (
              <Link
                href={`/cases/${caseId}/equipment_assignments/${assignment.id}/edit`}
                className="text-hpi-active-blue hover:text-hpi-blue text-xs font-medium"
              >
                Edit
              </Link>
            )}
          {assignment.currently_assigned ? (
            <span className="bg-hpi-dark-green/20 text-hpi-dark-green rounded px-2 py-1 text-xs">
              Assigned
            </span>
          ) : (
            <span className="bg-hpi-pale-gray text-hpi-dark-gray rounded px-2 py-1 text-xs">
              Returned
            </span>
          )}
        </div>
      </div>
      <div className="text-hpi-dark-gray mt-1 space-y-0.5 text-sm">
        {assignment.install_vehicle && (
          <div>
            <strong>Vehicle:</strong> {assignment.install_vehicle}
          </div>
        )}
        {assignment.install_gps_location && (
          <div>
            <strong>Device Placement:</strong> {assignment.install_gps_location}
          </div>
        )}
        {assignment.install_vehicle_location && (
          <div>
            <strong>Location of Vehicle when device was installed:</strong>{" "}
            {assignment.install_vehicle_location}
          </div>
        )}
        {assignment.returned_at && (
          <div className="mt-1">
            <strong>Returned:</strong> {assignment.returned_at}
            {assignment.removed_by && <span> by {assignment.removed_by}</span>}
          </div>
        )}
        {assignment.remove_vehicle_location && (
          <div>
            <strong>Location of Vehicle when device removed:</strong>{" "}
            {assignment.remove_vehicle_location}
          </div>
        )}
        <OdometerDisplay assignment={assignment} />
        {assignment.billable_amount != null &&
          assignment.billable_amount > 0 && (
            <div>
              <strong>Bill:</strong>{" "}
              {assignment.billable_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </div>
          )}
        {assignment.notes && (
          <div className="whitespace-pre-wrap">
            <strong>Notes:</strong> {assignment.notes}
          </div>
        )}
      </div>
    </li>
  )
}

export default function Index({
  case_record,
  assignments,
  summary,
  can_create,
  can_edit,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    {
      title: "Equipment",
      href: `/cases/${case_record.id}/equipment_assignments`,
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Equipment - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Equipment for {case_record.case_number}
          </h1>
          {can_create && (
            <Link
              href={`/cases/${case_record.id}/equipment_assignments/new`}
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white"
            >
              Assign Equipment
            </Link>
          )}
        </div>

        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-3 gap-4">
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Total Assignments</div>
            <div className="text-2xl font-bold">{summary.total}</div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Currently Assigned</div>
            <div className="text-2xl font-bold">
              {summary.currently_assigned}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Returned</div>
            <div className="text-2xl font-bold">{summary.returned}</div>
          </div>
        </div>

        {assignments.length > 0 ? (
          <div className="rounded-lg bg-white shadow">
            <h3 className="border-hpi-light-gray border-b px-4 py-3 text-center text-lg font-semibold">
              Equipment History
            </h3>
            <ul className="divide-hpi-light-gray divide-y px-4">
              {assignments.map((assignment) => (
                <AssignmentCard
                  key={`${assignment.entry_type}-${assignment.id}`}
                  assignment={assignment}
                  caseId={case_record.id}
                  canEdit={can_edit}
                />
              ))}
            </ul>
          </div>
        ) : (
          <div className="text-center">
            <h3 className="mb-4 text-lg font-semibold">Equipment</h3>
            <p className="text-hpi-med-gray">No Equipment entries found</p>
          </div>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
